// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/PartyDetailPage.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// residuals-first geometry, per-row undo, follow-up row and the seeded alias section carry.
// The alias section renders only while open (the predecessor's collapsed Accordion still
// mounted — and fetched — its panel; the RunInsights lesson, applied here too).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Undo2 } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { api, postJson } from '../../api/client'
import { usePartyDetail, usePeriodSummary, useRun } from '../../api/hooks'
import { sectionChip, splitGroups } from '../../api/partyDetailChips'
import { useRunResolutions } from '../../api/resolutions'
import { canReverse, reversalFacts } from '../../api/reversal'
import { MANUAL_MATCH } from '../../api/resolveRules'
import type { Residual, Resolution, SectionCut, TxnMatch } from '../../api/types'
import { AliasPicker } from '../../components/AliasPicker'
import { CategoryBadge, Chip, ResolutionBadge, type Tone } from '../../components/badges'
import { ChipStrip, StatChip } from '../../components/ChipStrip'
import { InfoTip } from '../../components/InfoTip'
import { MiniBars } from '../../components/MiniBars'
import { Btn } from '../../components/Modal'
import { Money } from '../../components/Money'
import { ResolveModal } from '../../components/ResolveModal'
import type { ResolveTarget } from '../../components/ResolveModal'
import { showToast } from '../../components/toast'
import { fmtInt } from '../../format'
import { useUserId } from '../../lib/identity'
import { stepUpPost } from '../../lib/stepup'

const NON_ACTIONABLE = new Set(['Matched - agreed', 'In 26AS with NIL TDS (informational)'])

const resolvable = (r: Resolution | undefined) =>
  !r || r.status === 'open' || r.status === 'rejected'

const MAX_GROUPS_SHOWN = 200
const MAX_RESIDUALS_SHOWN = 500

const th = 'px-2 py-1.5 text-left text-xs font-medium uppercase text-muted-foreground'
const td = 'px-2 py-1'

function SideTable({ rows }: { rows: TxnMatch[] }) {
  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">—</p>
  }
  return (
    <table className="w-full text-xs">
      <tbody>
        {rows.map((t, i) => (
          <tr key={i}>
            <td className="w-20 py-0.5">{t.date ?? '—'}</td>
            <td className="py-0.5">
              <p className="truncate text-xs text-muted-foreground" title={t.ref}>{t.ref || '—'}</p>
            </td>
            <td className="w-28 py-0.5 text-right"><Money v={t.amount} className="text-xs" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** The section-analysis card, collapsed to chips (density pass): the STATE —
 *  a rate outside its band, a section other than expected — is on the chip;
 *  the explanation table hides behind the click. */
function SectionChips({ sections }: { sections?: SectionCut[] | null }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  if (!sections || sections.length === 0) return null
  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        aria-label="Section analysis (26AS)"
        title="Section analysis — click for the full table"
        className="flex flex-wrap items-center gap-1"
        onClick={() => setOpen((s) => !s)}
      >
        {sections.map((s) => {
          const c = sectionChip(s)
          return <Chip key={s.section} tone={c.color as Tone}>{c.label}</Chip>
        })}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-[720px] max-w-[90vw] overflow-x-auto rounded-md border border-border bg-background p-3 shadow-lg">
          <p className="mb-1 text-sm font-semibold text-foreground">Section analysis (26AS)</p>
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className={th}>Section</th>
                <th className={th}>Txns</th>
                <th className={`${th} text-right`}>Amount paid/credited</th>
                <th className={`${th} text-right`}>TDS</th>
                <th className={th}>Observed %</th>
                <th className={th}>Band</th>
                <th className={th}>Rate</th>
                <th className={th}>Vs expected section</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s, i) => (
                <tr key={i} className="border-t border-border">
                  <td className={td}><Chip tone="gray" className="border border-border bg-transparent">{s.section}</Chip></td>
                  <td className={td}>{s.n}</td>
                  <td className={`${td} text-right`}><Money v={s.amount_26as} className="text-xs" /></td>
                  <td className={`${td} text-right`}><Money v={s.tds_26as} className="text-xs" /></td>
                  <td className={td}>{s.observed_pct ?? '—'}</td>
                  <td className={`${td} text-muted-foreground`}>{s.rate_band || '—'}</td>
                  <td className={td}>
                    <Chip tone={s.rate_flag === 'OUTSIDE BAND' ? 'orange' : s.rate_flag === 'ok' ? 'green' : 'gray'}>
                      {s.rate_flag}
                    </Chip>
                  </td>
                  <td className={td}>
                    {s.section_flag ? (
                      <Chip tone={s.section_flag.startsWith('DIFFERENT') ? 'red' : 'green'}>
                        {s.section_flag}
                      </Chip>
                    ) : (
                      <span className="text-xs text-muted-foreground">no expectation set</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">
            Sales-per-books comparison arrives once the sales data source is finalised.
          </p>
        </div>
      )}
    </span>
  )
}

function MatchGroups({ matches }: { matches: TxnMatch[] }) {
  const groups = splitGroups(matches)
  const shown = groups.slice(0, MAX_GROUPS_SHOWN)

  if (groups.length === 0) {
    return <p className="text-sm text-muted-foreground">No transaction-level matches for this party.</p>
  }
  return (
    <div className="max-h-[520px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-background">
          <tr>
            <th className={`${th} w-11`}>#</th>
            <th className={th}>Type</th>
            <th className={th}>26AS date</th>
            <th className={th}>Ref</th>
            <th className={`${th} text-right`}>Amount</th>
            <th className={th}>Ledger date</th>
            <th className={th}>Ref</th>
            <th className={`${th} text-right`}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((g) =>
            g.simple ? (
              <tr key={g.id} className="border-t border-border">
                <td className={`${td} text-muted-foreground`}>#{g.id}</td>
                <td className={td}><Chip tone="blue">{g.type}</Chip></td>
                <td className={`${td} whitespace-nowrap`}>{g.s26[0].date ?? '—'}</td>
                <td className={td} style={{ maxWidth: 150 }}>
                  <p className="truncate text-xs text-muted-foreground" title={g.s26[0].ref}>{g.s26[0].ref || '—'}</p>
                </td>
                <td className={`${td} text-right`}><Money v={g.s26[0].amount} className="text-xs" /></td>
                <td className={`${td} whitespace-nowrap`}>{g.led[0].date ?? '—'}</td>
                <td className={td} style={{ maxWidth: 150 }}>
                  <p className="truncate text-xs text-muted-foreground" title={g.led[0].ref}>{g.led[0].ref || '—'}</p>
                </td>
                <td className={`${td} text-right`}><Money v={g.led[0].amount} className="text-xs" /></td>
              </tr>
            ) : (
              <tr key={g.id} className="border-t border-border">
                <td className={`${td} text-muted-foreground`}>#{g.id}</td>
                <td className={td}><Chip tone="blue">{g.type}</Chip></td>
                <td className={td} colSpan={6}>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="mb-0.5 text-xs font-semibold text-indigo-800">
                        26AS ({g.s26.length})
                      </p>
                      <SideTable rows={g.s26} />
                    </div>
                    <div>
                      <p className="mb-0.5 text-xs font-semibold text-teal-800">
                        Ledger ({g.led.length})
                      </p>
                      <SideTable rows={g.led} />
                    </div>
                  </div>
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  )
}

interface ResidualsProps {
  residuals: Residual[]
  resByFp: Map<string, Resolution>
  selected: Set<string>
  onToggle: (fp: string) => void
  reversible: (r: Resolution | undefined) => boolean
  onReverse: (r: Resolution) => void
  reverseBusy: string | null
}

function Residuals({
  residuals, resByFp, selected, onToggle, reversible, onReverse, reverseBusy,
}: ResidualsProps) {
  if (residuals.length === 0) {
    return <p className="text-sm text-muted-foreground">No unmatched transactions - everything paired up.</p>
  }
  const shown = residuals.slice(0, MAX_RESIDUALS_SHOWN)
  return (
    <div className="max-h-[520px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-background">
          <tr>
            <th className={`${th} w-8`} />
            <th className={th}>Side</th>
            <th className={th}>Date</th>
            <th className={th}>Ref</th>
            <th className={`${th} text-right`}>Amount</th>
            <th className={th}>Classification</th>
            <th className={th}>Resolution</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => {
            const res = resByFp.get(r.fingerprint)
            return (
              <tr key={i} className="border-t border-border">
                <td className={td}>
                  <input
                    type="checkbox"
                    checked={selected.has(r.fingerprint)}
                    disabled={!resolvable(res)}
                    onChange={() => onToggle(r.fingerprint)}
                    aria-label="select residual"
                  />
                </td>
                <td className={td}>
                  <Chip tone={r.side.startsWith('26AS') ? 'blue' : 'teal'}>
                    {r.side.startsWith('26AS') ? '26AS' : 'Books'}
                  </Chip>
                </td>
                <td className={`${td} whitespace-nowrap`}>{r.date ?? '—'}</td>
                <td className={td} style={{ maxWidth: 220 }}>
                  <p className="truncate text-xs text-muted-foreground" title={r.ref}>{r.ref || '—'}</p>
                </td>
                <td className={`${td} text-right`}><Money v={r.amount} className="text-xs" /></td>
                <td className={td}>
                  {r.classification ? (
                    <Chip tone="violet" className="border border-violet-300 bg-transparent">
                      {r.classification}
                    </Chip>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className={td}>
                  <span className="flex flex-nowrap items-center gap-1">
                    <ResolutionBadge resolution={res} />
                    {/* the Cleared tab is gone — undo lives beside the
                        disposition it undoes */}
                    {res != null && reversible(res) && (
                      <button
                        type="button"
                        className="rounded p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-50"
                        aria-label="undo disposition"
                        title={res.reason_code === MANUAL_MATCH ? 'Un-match' : 'Un-explain'}
                        disabled={reverseBusy === res.id}
                        onClick={() => onReverse(res)}
                      >
                        <Undo2 size={13} />
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

interface FollowUpItemT {
  id: string
  party_name: string
  owner_id: string | null
  owner_name: string | null
  status: string
  note: string | null
  updated_at: string | null
}

const FU_STATUSES = ['open', 'contacted', 'response_received', 'escalated', 'closed']

const selCls = 'rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary'

/** Chasing lives WHERE THE PARTY IS. One compact row — owner, chase status,
 *  note — over the existing follow-ups CRUD; the register export keeps its
 *  Reports door. Owners are the org members already on the register, rendered
 *  by the identity projection (the platform owns identity, ADR-0002). */
function PartyFollowUp({ clientId, party }: { clientId: string; party: string }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['follow-ups', clientId],
    queryFn: () => api<{ items: FollowUpItemT[]
      owners: { id: string; name: string }[] }>(
      `/clients/${clientId}/follow-ups`),
    enabled: Boolean(clientId),
  })
  const [note, setNote] = useState<string | null>(null)
  const mine = q.data?.items.find((i) => i.party_name === party)
  const save = useMutation({
    mutationFn: (patch: { status?: string; owner_id?: string | null; note?: string }) =>
      mine
        ? api(`/follow-ups/${mine.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
          })
        : postJson(`/clients/${clientId}/follow-ups`, { party_name: party, ...patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['follow-ups'] })
      showToast({ color: 'green', title: 'Follow-up noted.' })
    },
    onError: (e) => showToast({ color: 'red', title: String(e) }),
  })
  if (!clientId) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-semibold text-muted-foreground">Follow-up</span>
      <select
        className={`${selCls} w-44`}
        value={mine?.owner_id ?? ''}
        onChange={(e) => save.mutate({ owner_id: e.target.value || null })}
        aria-label="Follow-up owner"
      >
        <option value="">owner</option>
        {(q.data?.owners ?? []).map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <select
        className={`${selCls} w-40`}
        value={mine?.status ?? 'open'}
        onChange={(e) => e.target.value && save.mutate({ status: e.target.value })}
        aria-label="Follow-up status"
      >
        {FU_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <input
        className={`${selCls} w-64`}
        placeholder="note - what was said, what is awaited"
        value={note ?? mine?.note ?? ''}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => {
          if (note != null && note !== (mine?.note ?? '')) save.mutate({ note })
        }}
      />
      {mine?.updated_at && (
        <span className="text-[10px] text-muted-foreground">updated {mine.updated_at.slice(0, 10)}</span>
      )}
    </div>
  )
}

export function PartyDetailPage() {
  const params = useParams()
  const [search] = useSearchParams()
  // workspace route carries the run in ?run=
  const runId = search.get('run') ?? ''
  const partyId = params.partyId ?? ''
  const detail = usePartyDetail(runId, partyId)
  const runQ = useRun(runId)
  const summaryQ = usePeriodSummary(params.periodId ?? '')
  const resolutionsQ = useRunResolutions(runId, true)
  const resByFp = useMemo(
    () => new Map((resolutionsQ.data ?? []).map((r) => [r.fingerprint, r])),
    [resolutionsQ.data],
  )
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [modal, setModal] = useState<{ kind: 'party' | 'residual'; targets: ResolveTarget[] } | null>(
    null,
  )
  const [aliasOpen, setAliasOpen] = useState(false)
  const userId = useUserId()
  const qc = useQueryClient()
  const [reverseBusy, setReverseBusy] = useState<string | null>(null)
  const reverse = useMutation({
    // reverse is one of the step-up-gated review verbs (guide §7.2)
    mutationFn: (id: string) =>
      stepUpPost(`/resolutions/${id}/reverse`, { comment: 'reversed from party page' }),
    onSuccess: () => {
      showToast({ color: 'blue', title: 'Disposition reversed — entries re-opened.' })
      for (const k of ['party', 'resolutions', 'cleared', 'reconciliation', 'difference',
        'difference-summary', 'difference-parties', 'match-suggestions']) {
        void qc.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) => showToast({ color: 'red', title: String(e) }),
    onSettled: () => setReverseBusy(null),
  })
  const reversible = (r: Resolution | undefined) =>
    r != null && canReverse(reversalFacts(r), userId)
  const onReverse = (r: Resolution) => {
    setReverseBusy(r.id)
    reverse.mutate(r.id)
  }

  if (detail.isPending) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
  }
  if (detail.isError || !detail.data) {
    return (
      <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
        Could not load party: {String(detail.error)}
      </p>
    )
  }

  const { party, txn_matches, residuals, residuals_by_month } = detail.data
  const partyRes = resByFp.get(party.fingerprint)
  const toggle = (fp: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(fp)) next.delete(fp)
      else next.add(fp)
      return next
    })
  const selectedTargets: ResolveTarget[] = residuals
    .filter((r) => selected.has(r.fingerprint))
    .map((r) => ({
      fingerprint: r.fingerprint,
      label: `${r.side} · ${r.date ?? '?'} · ${r.ref}`,
      amount: r.amount,
      side: r.side,
    }))
  const nGroups = new Set(txn_matches.map((m) => m.group)).size

  return (
    <div className="space-y-3">
      {/* Header, density-pass shape: one title row (name · category · ⓘ ·
          resolve) + one chip row (the four figures + section state). The
          reference lines — match basis, txn counts, TANs, ledger names,
          fingerprint — read once, live behind the ⓘ. */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/w/${params.clientId}/${params.periodId}/reconciliation?run=${runId}`}
            className="text-sm text-primary hover:underline"
          >
            ← Back
          </Link>
          <h1 className="text-base font-semibold text-foreground">{party.party}</h1>
          <CategoryBadge category={party.category} full />
          <InfoTip w={440}>
            match: {party.match_basis}
            {party.score != null ? ` (score ${party.score.toFixed(0)})` : ''} · txns{' '}
            {fmtInt(party.n26)} in 26AS / {fmtInt(party.nled)} in ledger
            {(party.tans?.length ?? 0) > 0 && (
              <>
                <br />
                TANs: {party.tans?.join(' · ')}
              </>
            )}
            {(party.ledger_names?.length ?? 0) > 0 && (
              <>
                <br />
                ledger names: {party.ledger_names?.join(' · ')}
              </>
            )}
            <br />
            fingerprint {party.fingerprint.slice(0, 16)}… — stable across re-runs; resolutions
            carry forward automatically.
          </InfoTip>
          {!NON_ACTIONABLE.has(party.category) && (
            <>
              <ResolutionBadge resolution={partyRes} />
              {partyRes != null && reversible(partyRes) && (
                <Btn
                  size="xs"
                  kind="subtle"
                  loading={reverseBusy === partyRes.id}
                  onClick={() => onReverse(partyRes)}
                >
                  <Undo2 size={13} />
                  {partyRes.reason_code === MANUAL_MATCH ? 'Un-match' : 'Un-explain'}
                </Btn>
              )}
              {resolvable(partyRes) && (
                <Btn
                  size="xs"
                  onClick={() =>
                    setModal({
                      kind: 'party',
                      targets: [
                        { fingerprint: party.fingerprint, label: party.party, amount: party.diff },
                      ],
                    })
                  }
                >
                  Resolve party
                </Btn>
              )}
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <ChipStrip>
            <StatChip label="TDS 26AS" value={party.tds_26as} />
            <StatChip label="Amount 26AS" value={party.amt_26as} />
            <StatChip label="Ledger Dr" value={party.ledger_dr} />
            <StatChip label="Difference" value={party.diff} colorize />
          </ChipStrip>
          <SectionChips sections={party.sections} />
        </div>
        <PartyFollowUp
          clientId={summaryQ.data?.client.id ?? ''}
          party={party.party}
        />
      </div>

      {/* party-grain books — the transaction tables would be showing sums
          pretending to be entries; one sentence instead. Resolve-party stays:
          the party-level explain IS the workflow in this mode. */}
      {runQ.data?.book_grain === 'party' && (
        <p className="rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
          Books carry party-level sums only — this run reconciles at party
          level. Use <b>Resolve party</b> to explain the difference;
          transaction matching is unavailable.
        </p>
      )}
      {/* Residuals first: they carry the checkboxes and the Resolve button —
          the act-on surface owns the primary column. Matched groups are
          reference reading and sit to the right. */}
      {runQ.data?.book_grain !== 'party' && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="rounded-lg border border-border p-2.5">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="flex items-baseline gap-2">
                <h2 className="text-sm font-semibold text-foreground">Unmatched residuals</h2>
                {residuals.length > MAX_RESIDUALS_SHOWN && (
                  <span className="text-xs text-muted-foreground">
                    showing {MAX_RESIDUALS_SHOWN} of {fmtInt(residuals.length)} (largest first)
                  </span>
                )}
              </div>
              {selected.size > 0 && (
                <Btn
                  size="xs"
                  kind="primary"
                  onClick={() => setModal({ kind: 'residual', targets: selectedTargets })}
                >
                  Resolve {selected.size} selected
                </Btn>
              )}
            </div>
            <Residuals
              residuals={residuals}
              resByFp={resByFp}
              selected={selected}
              onToggle={toggle}
              reversible={reversible}
              onReverse={onReverse}
              reverseBusy={reverseBusy}
            />
          </div>
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-2.5">
              <div className="mb-1 flex items-baseline gap-2">
                <h2 className="text-sm font-semibold text-foreground">Matched transactions (26AS vs ledger)</h2>
                {nGroups > MAX_GROUPS_SHOWN && (
                  <span className="text-xs text-muted-foreground">
                    showing {MAX_GROUPS_SHOWN} of {fmtInt(nGroups)} groups — full list in the
                    workbook
                  </span>
                )}
              </div>
              <MatchGroups matches={txn_matches} />
            </div>
            <div className="rounded-lg border border-border p-2.5">
              <h2 className="mb-1 text-sm font-semibold text-foreground">Residuals by month</h2>
              <MiniBars data={residuals_by_month} />
            </div>
          </div>
        </div>
      )}

      {/* The same in-flow question as the grid, asked where the party is:
          is one of the run's other names this party too? Seeded with what
          this party already owns, so the reconciler is not made to re-find
          the party they are looking at. */}
      <div className="rounded-lg border border-border">
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
          onClick={() => setAliasOpen((s) => !s)}
        >
          {aliasOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="text-sm font-semibold text-foreground">Same party under another name?</span>
        </button>
        {aliasOpen && (
          <div className="border-t border-border p-3">
            <AliasPicker runId={runId} seedParty={party.party} embedded />
          </div>
        )}
      </div>

      {modal && runQ.data && (
        <ResolveModal
          opened
          onClose={() => {
            setModal(null)
            setSelected(new Set())
          }}
          runId={runId}
          periodId={runQ.data.period_id}
          itemKind={modal.kind}
          targets={modal.targets}
          existing={modal.kind === 'party' ? (partyRes ?? null) : null}
        />
      )}
    </div>
  )
}
